import { useState } from 'react'
import { router } from '@inertiajs/react'
import { useFlashToast } from '../../hooks/useFlashToast'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faShieldHalved, faMagnifyingGlass, faCircleCheck, faCircleXmark, faClockRotateLeft
} from '@fortawesome/free-solid-svg-icons'
import AdminSidebar from '../../components/admin/Sidebar'
import AdminTopbar from '../../components/admin/Topbar'
import SuperadminSidebar from '../../components/superadmin/Sidebar'
import SuperadminTopbar from '../../components/superadmin/Topbar'

export default function AuditLogsIndex({
  current_user,
  portal = 'admin',
  audit_logs_path,
  filters = {},
  stats = {},
  logs = [],
}) {
  useFlashToast()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [query, setQuery] = useState(filters.query || '')
  const dark = portal === 'superadmin'
  const Sidebar = dark ? SuperadminSidebar : AdminSidebar
  const Topbar = dark ? SuperadminTopbar : AdminTopbar

  const apply = (success = filters.success) => {
    router.get(audit_logs_path, { q: query, success }, { preserveState: true })
  }

  return (
    <div className={`flex h-screen font-sans overflow-hidden ${dark ? 'bg-gray-950 text-gray-100' : 'bg-gray-50 text-gray-900'}`}>
      <Sidebar current_user={current_user} open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Topbar current_user={current_user} onMenuToggle={() => setSidebarOpen(true)} />
        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-1">
              <FontAwesomeIcon icon={faClockRotateLeft} className={`w-4 h-4 ${dark ? 'text-indigo-400' : 'text-blue-600'}`} />
              <h1 className={`text-xl font-bold ${dark ? 'text-white' : 'text-gray-900'}`}>Audit Log Login</h1>
            </div>
            <p className={`text-sm ${dark ? 'text-gray-500' : 'text-gray-500'}`}>Riwayat percobaan masuk ke sistem</p>
          </div>

          <div className="grid grid-cols-3 gap-3 mb-6 max-w-xl">
            {[
              { label: 'Total', value: stats.total, color: dark ? 'text-gray-200' : 'text-gray-800' },
              { label: 'Berhasil', value: stats.success, color: 'text-emerald-500' },
              { label: 'Gagal', value: stats.failed, color: 'text-red-500' },
            ].map(s => (
              <div key={s.label} className={`rounded-xl border p-4 ${dark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
                <div className={`text-2xl font-bold ${s.color}`}>{s.value ?? 0}</div>
                <div className={`text-xs ${dark ? 'text-gray-500' : 'text-gray-500'}`}>{s.label}</div>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-2 mb-4">
            {[
              { key: '', label: 'Semua' },
              { key: 'true', label: 'Berhasil' },
              { key: 'false', label: 'Gagal' },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => apply(tab.key)}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  filters.success === tab.key
                    ? dark ? 'bg-indigo-600 text-white' : 'bg-blue-600 text-white'
                    : dark ? 'bg-gray-900 text-gray-400 border border-gray-800 hover:bg-gray-800' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <form onSubmit={(e) => { e.preventDefault(); apply() }} className="mb-4 max-w-md">
            <div className="relative">
              <FontAwesomeIcon icon={faMagnifyingGlass} className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${dark ? 'text-gray-500' : 'text-gray-400'}`} />
              <input
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Cari username/email atau IP..."
                className={`w-full pl-9 pr-4 py-2 text-sm rounded-lg border outline-none ${dark ? 'bg-gray-900 border-gray-800 text-gray-200 placeholder-gray-600 focus:ring-indigo-500' : 'bg-white border-gray-200 focus:ring-blue-500'}`}
              />
            </div>
          </form>

          <div className={`rounded-xl border overflow-hidden ${dark ? 'border-gray-800' : 'border-gray-200'}`}>
            <table className="w-full text-sm">
              <thead className={dark ? 'bg-gray-900 text-gray-400' : 'bg-gray-50 text-gray-500'}>
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Waktu</th>
                  <th className="text-left px-4 py-3 font-semibold">Login</th>
                  <th className="text-left px-4 py-3 font-semibold">Status</th>
                  <th className="text-left px-4 py-3 font-semibold hidden md:table-cell">IP</th>
                  <th className="text-left px-4 py-3 font-semibold hidden lg:table-cell">Alasan</th>
                </tr>
              </thead>
              <tbody className={dark ? 'divide-y divide-gray-800' : 'divide-y divide-gray-100 bg-white'}>
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan={5} className={`px-4 py-8 text-center ${dark ? 'text-gray-500' : 'text-gray-400'}`}>Tidak ada data.</td>
                  </tr>
                ) : logs.map(log => (
                  <tr key={log.id} className={dark ? 'hover:bg-gray-900/80' : 'hover:bg-gray-50'}>
                    <td className={`px-4 py-3 whitespace-nowrap ${dark ? 'text-gray-300' : 'text-gray-700'}`}>{log.created_at}</td>
                    <td className="px-4 py-3">
                      <div className={`font-medium ${dark ? 'text-gray-200' : 'text-gray-900'}`}>{log.login_value}</div>
                      {log.user_name && <div className={`text-xs ${dark ? 'text-gray-500' : 'text-gray-400'}`}>{log.user_name}</div>}
                    </td>
                    <td className="px-4 py-3">
                      {log.success ? (
                        <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-semibold">
                          <FontAwesomeIcon icon={faCircleCheck} className="w-3 h-3" /> Berhasil
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-red-500 text-xs font-semibold">
                          <FontAwesomeIcon icon={faCircleXmark} className="w-3 h-3" /> Gagal
                        </span>
                      )}
                    </td>
                    <td className={`px-4 py-3 hidden md:table-cell font-mono text-xs ${dark ? 'text-gray-400' : 'text-gray-500'}`}>{log.ip_address || '—'}</td>
                    <td className={`px-4 py-3 hidden lg:table-cell text-xs ${dark ? 'text-gray-500' : 'text-gray-500'}`}>{log.failure_reason || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </main>
      </div>
    </div>
  )
}
